'use client'

import * as React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/navigation'
import {
  ArrowLeftIcon,
  Blocks,
  Building2,
  Cable,
  ChartColumn,
  ChartNoAxesCombined,
  CheckIcon,
  ChevronDownIcon,
  CreditCard,
  FileTextIcon,
  FolderOpenIcon,
  Globe2Icon,
  History,
  ImagesIcon,
  Link2Icon,
  ListChecks,
  MessageSquare,
  MessagesSquare,
  PlaneTakeoffIcon,
  PlugZap,
  Puzzle,
  RadarIcon,
  RocketIcon,
  Settings2,
  Zap,
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import { ClientSwitcher } from '@/components/client-switcher'
import { NavUser } from '@/components/nav-user'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { useOrgAdminGate } from '@/hooks/use-org-access'
import { useUserContext } from '@/hooks/use-user-context'
import { brandKeyFor, useLastChatStore } from '@/stores/last-chat-store'
import { useBrandWorkspaces } from '@/hooks/use-brand-workspaces'
import { useOptionalActiveOrg } from '@/providers/active-scope-provider'
import {
  NAV_WORKSPACES,
  getNavWorkspace,
  storeNavWorkspace,
  useStoredNavWorkspace,
  type NavWorkspaceId,
  type NavWorkspaceItemKey,
} from '@/lib/nav-workspaces'
import { useProductBrand } from '@/providers/product-brand-provider'
import { resolveProductName, resolveRailLogo } from '@/lib/partner-branding'
import {
  buildRailHrefs,
  pickFallbackBrandSlug,
  type RailHrefs,
} from '@/lib/rail-hrefs'
import { cn } from '@/lib/utils'

// ── Shared route + active-state model ──────────────────────────────────────

type RailModel = RailHrefs

type RailRow = {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  active: boolean
}

// Icons for the workspace-zone rows, keyed by item key (the rows themselves —
// order, labels, hrefs, active state — are data from `NAV_WORKSPACES` +
// `buildRailHrefs`).
const NAV_WORKSPACE_ICONS: Record<
  NavWorkspaceItemKey,
  React.ComponentType<{ className?: string }>
> = {
  dashboard: ChartNoAxesCombined,
  adLauncher: RocketIcon,
  creatives: ImagesIcon,
  visibility: RadarIcon,
  citations: Link2Icon,
  reports: FileTextIcon,
  sites: Globe2Icon,
}

/**
 * The rows that swap with the workspace zone. With the switcher hidden this is
 * exactly the Ads rows (Dashboard, Ad Launcher, Creatives).
 */
function workspaceRows(
  model: RailModel,
  t: ReturnType<typeof useTranslations>,
): RailRow[] {
  const byKey: Record<NavWorkspaceItemKey, { href: string; active: boolean }> =
    {
      dashboard: { href: model.dashboardHref, active: model.isDashboardActive },
      adLauncher: {
        href: model.adLauncherHref,
        active: model.isAdLauncherActive,
      },
      creatives: { href: model.creativesHref, active: model.isCreativesActive },
      visibility: {
        href: model.visibilityHref,
        active: model.isVisibilityActive,
      },
      citations: { href: model.citationsHref, active: model.isCitationsActive },
      reports: { href: model.reportsHref, active: model.isReportsActive },
      sites: { href: model.sitesHref, active: model.isSitesActive },
    }
  return getNavWorkspace(model.navWorkspace).items.map(item => ({
    ...byKey[item.key],
    label: t(item.labelKey),
    icon: NAV_WORKSPACE_ICONS[item.key],
  }))
}

/**
 * The whole brand menu on a toolfare host: toolfare sells Built-in
 * capabilities to AI apps, so the rail lists the AI apps page, billing and the
 * approvals queue and nothing of Soku's (`lib/connect/toolfare-console.ts`
 * redirects the rest).
 */
function toolfareRows(
  model: RailModel,
  t: ReturnType<typeof useTranslations>,
): RailRow[] {
  const billing: RailRow[] = model.showBilling
    ? [
        {
          href: model.billingHref,
          label: t('orgBilling'),
          icon: CreditCard,
          active: model.isBillingActive,
        },
      ]
    : []
  return [
    {
      href: model.mcpCliHref,
      label: t('mcpCli'),
      icon: PlugZap,
      active: model.isMcpCliActive,
    },
    ...billing,
    {
      href: model.approvalsHref,
      label: t('approvals'),
      icon: ListChecks,
      active: model.isApprovalsActive,
    },
  ]
}

// Icons for the org-workspace menu, keyed by item slug (the menu itself —
// sections, slugs, hrefs, active state — is data from `buildRailHrefs`).
const ORG_RAIL_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  overview: Building2,
  chat: MessageSquare,
  chats: MessagesSquare,
  usage: ChartColumn,
  activity: History,
  billing: CreditCard,
}

function useRailModel(): RailModel {
  const pathname = usePathname()
  // The URL is the single source of truth for the active brand. On non-brand
  // surfaces (`/o/{slug}/b/new`, …) this is null; `buildRailHrefs` then falls
  // back to the org's first workspace so links never point at a removed root
  // path. `useBrandWorkspaces` shares the ClientSwitcher's SWR cache key, so
  // this adds no extra request.
  const workspace = useOptionalCurrentBrand()
  const org = useOptionalActiveOrg()
  const { product } = useProductBrand()
  const { data: brandsData } = useBrandWorkspaces()
  // The Organization Workspace menu is admin-only; a member never sees the
  // entries, and the SSR gate 404s the routes behind them.
  const orgWorkspaceVisible = useOrgAdminGate()

  const firstBrandSlug = React.useMemo(
    () => (workspace ? null : pickFallbackBrandSlug(brandsData)),
    [workspace, brandsData],
  )

  const lastChatId = useLastChatStore(state =>
    workspace
      ? state.lastByBrand[brandKeyFor(workspace.orgSlug, workspace.brandSlug)]
      : undefined,
  )
  const storedNavWorkspace = useStoredNavWorkspace(
    workspace?.orgSlug ?? null,
    workspace?.brandSlug ?? null,
  )

  return buildRailHrefs({
    brand: workspace
      ? { orgSlug: workspace.orgSlug, brandSlug: workspace.brandSlug }
      : null,
    orgSlug: workspace?.orgSlug ?? org?.slug ?? null,
    firstBrandSlug,
    pathname,
    lastChatId,
    orgWorkspaceVisible,
    // Two independent gates: the org opted in (org admin, Settings → Labs) and
    // the brand is entitled (platform-admin controlled; every autopilot
    // endpoint 404s without it). The entry appears only when both hold.
    autopilotEnabled:
      (org?.autopilotOptedIn ?? false) &&
      (workspace?.autopilotVisible ?? false),
    // One gate: the org opted in (org admin, Settings → Labs). Every GEO
    // endpoint 404s without it, so there is no per-brand entitlement to AND.
    geoWorkspaceEnabled: org?.geoWorkspaceOptedIn ?? false,
    storedNavWorkspace,
    toolfare: product === 'toolfare',
  })
}

// ── Desktop rail pieces ─────────────────────────────────────────────────────

/**
 * The mark at the top of the rail.
 *
 * Reads the partner off the active org rather than taking a prop: both call
 * sites (desktop rail, mobile sidebar) then need no change, and there is one
 * place that decides what the mark is. A workspace with no partner — including
 * one whose partnership has ended, which arrives as an absent partner — renders
 * Soku's own mark.
 */
function SokuLogoMark({ className }: { className?: string }) {
  const org = useOptionalActiveOrg()
  const brand = useProductBrand()
  const partner = org?.partner ?? null
  return (
    <img
      src={resolveRailLogo(partner, brand)}
      alt={resolveProductName(partner, brand)}
      className={cn('size-6 shrink-0', className)}
    />
  )
}

// One shared row shape for every nav entry, so the rail reads as a single list
// rather than a set of differently-weighted controls.
const ROW =
  'text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-sidebar-foreground flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors'
const ROW_ACTIVE =
  'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
// A row label in the collapsible rail: fades with the same 200ms linear curve
// the primitive's width transition uses, so text dissolves while the rail
// slides instead of vanishing a frame before it. text-clip (not ellipsis)
// while collapsed keeps a stray "…" from ghosting through the fade.
// The exit row's arrow points "back", so it mirrors in right-to-left layouts.
function BackArrowIcon({ className }: { className?: string }) {
  return <ArrowLeftIcon className={cn(className, 'rtl:-scale-x-100')} />
}

const LABEL =
  'min-w-0 truncate transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:text-clip group-data-[collapsible=icon]:opacity-0'

function NavRow({
  href,
  label,
  icon: Icon,
  active,
  className,
}: {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  active: boolean
  className?: string
}) {
  // Collapsed (icon) mode: the label fades to a right-side tooltip — same
  // gating the sidebar primitive's own menu button uses. The tooltip stays
  // mounted but hidden while expanded so hover on a labeled row never grows a
  // redundant bubble. The label fades (opacity) rather than unmounting, and
  // the icon keeps its left alignment: every layout property then rides the
  // rail's 200ms width transition instead of snapping to icon-mode first.
  const { state, isMobile } = useSidebar()
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link href={href} className={cn(ROW, active && ROW_ACTIVE, className)}>
          <Icon className="size-4 shrink-0" />
          <span className={cn(LABEL, 'flex-1')}>{label}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" hidden={state !== 'collapsed' || isMobile}>
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

// How long the Setup flyout survives the pointer leaving it — long enough to
// cross the gap between the row and the menu, short enough that sweeping past
// the row on the way elsewhere doesn't leave a menu hanging open.
const SETUP_FLYOUT_CLOSE_DELAY_MS = 150

type SetupItem = {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  active: boolean
}

/**
 * The "Setup" tail of the rail: how you make the agent yours — connect your own
 * accounts, install your own skills, reach it from your own AI app over MCP or
 * the Soku CLI. Everything above it is a place you do work; everything in it
 * is a place you shape the tool.
 *
 * The parent navigates nowhere — there is no "Setup" page — so its label
 * names what it holds ("Connections · Skills") rather than an umbrella word.
 * Not the three item names joined: those overflow the 15rem rail. Hovering it
 * opens a flyout to the right with the three destinations, the same shape in
 * both rail states. The rows stay one hover away without pushing the rail's
 * other rows around, and the parent carries the active highlight for whichever
 * child is current, since the children are never inline.
 *
 * Still a real `DropdownMenu`: click and Enter/Space open it too, and arrow
 * keys walk the items, so hover is an accelerator rather than the only way in.
 */
function SetupSection({ label, items }: { label: string; items: SetupItem[] }) {
  const hasActiveChild = items.some(item => item.active)
  const [open, setOpen] = React.useState(false)
  // Set when the pointer (not the keyboard) opened the menu: a hover-close
  // must not yank focus back to the row, but a keyboard close should.
  const openedByHover = React.useRef(false)
  const closeTimer = React.useRef<number | null>(null)

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openOnHover = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    cancelClose()
    openedByHover.current = true
    setOpen(true)
  }
  const closeOnLeave = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    cancelClose()
    closeTimer.current = window.setTimeout(
      () => setOpen(false),
      SETUP_FLYOUT_CLOSE_DELAY_MS,
    )
  }
  React.useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    },
    [],
  )

  return (
    <DropdownMenu
      modal={false}
      open={open}
      onOpenChange={next => {
        cancelClose()
        if (next) openedByHover.current = false
        setOpen(next)
      }}
    >
      <DropdownMenuTrigger asChild>
        {/* A real `button`, not a styled `span`: `asChild` forwards the
            trigger's props, but focusability and Enter/Space activation are
            the element's own. Same ROW geometry as every other nav row. */}
        <button
          type="button"
          className={cn(ROW, 'cursor-pointer', hasActiveChild && ROW_ACTIVE)}
          onPointerEnter={openOnHover}
          onPointerLeave={closeOnLeave}
          onPointerDown={event => {
            // Radix toggles on pointerdown, so a click on a row the hover
            // already opened would shut it. Keep it open instead.
            if (open && event.pointerType === 'mouse') event.preventDefault()
          }}
        >
          <Blocks className="size-4 shrink-0" />
          <span className={cn(LABEL, 'flex-1')}>{label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        className="min-w-44"
        onPointerEnter={openOnHover}
        onPointerLeave={closeOnLeave}
        onCloseAutoFocus={event => {
          if (openedByHover.current) event.preventDefault()
        }}
      >
        {items.map(({ href, label: itemLabel, icon: Icon, active }) => (
          <DropdownMenuItem
            key={href}
            asChild
            className={cn(
              active && 'bg-accent text-accent-foreground font-medium',
            )}
          >
            <Link href={href} onClick={() => setOpen(false)}>
              <Icon className="size-4 shrink-0" />
              {itemLabel}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Brand lockup at the top of the rail: the mark plus the product name.
 *
 * The name is partner-aware (a co-branded workspace sees its own). It carries
 * no product suffix — there is one product line, so "Soku Ads" named a
 * distinction that does not exist yet.
 *
 * Links home (the brand's chat). It used to open the getting-started
 * checklist, retired 2026-09: nobody used it, and the surfaces it pointed at
 * now carry their own first-run guidance.
 */
function ProductWordmark({ homeHref }: { homeHref: string }) {
  const org = useOptionalActiveOrg()
  const brand = useProductBrand()
  const partner = org?.partner ?? null
  return (
    <Link
      href={homeHref}
      aria-label={resolveProductName(partner, brand)}
      // Geometry copied from a nav row: 32px tall, px-2 inside a px-2
      // container, gap-2 — so the hover rectangle, the icon column and the
      // label column all land where every row below puts them, and the
      // mark sits on the icon column's x in both rail states (no
      // re-centering snap on collapse). Width hugs the lockup (min-w-0
      // still lets a long partner name truncate) instead of stretching to
      // the collapse trigger — a full-width link made aiming at the
      // trigger risk a stray navigation.
      className="hover:bg-sidebar-hover flex h-8 min-w-0 items-center gap-2 self-start rounded-md px-2 transition-colors"
    >
      {/* The mark is 20px of artwork in a 16px slot — the same slot a nav
              icon occupies — so it overhangs 2px each side and stays centred on
              the icon column instead of pushing the wordmark right. Sizing it
              down to 16px instead would leave the brand lighter than the nav.

              Up 1px: the artwork itself is symmetric (its ink is centred in the
              PNG), but Editorial New's cap-height centre sits above its
              line-box centre, so a geometrically centred mark reads low beside
              the wordmark. With the wordmark 1px down, this keeps the 2px
              separation uiux-demo tuned. */}
      <span className="flex size-4 shrink-0 -translate-y-[1px] items-center justify-center">
        <SokuLogoMark className="size-5 max-w-none" />
      </span>
      {/* `font-serif` is Editorial New, the product serif. Editorial New
              ships nothing between Regular and Bold, so this stays at 400.
              Down 1px: the serif's glyphs sit high in their line box, so a
              flex-centred line reads as riding above the mark. */}
      <span
        className={cn(
          LABEL,
          'translate-y-[1px] font-serif text-[19px] leading-7 font-normal tracking-tight text-[#ff7700]',
        )}
      >
        {resolveProductName(partner, brand)}
      </span>
    </Link>
  )
}

/**
 * The wordmark as a workspace switcher (Labs: GEO workspace). Same geometry as
 * `ProductWordmark`, with the current zone's name after the product name and a
 * menu listing every zone with its full description. Picking a zone remembers
 * it for this brand and lands on the zone's first row.
 */
function NavWorkspaceSwitcher({
  model,
  t,
}: {
  model: RailModel
  t: ReturnType<typeof useTranslations>
}) {
  const org = useOptionalActiveOrg()
  const brand = useProductBrand()
  const partner = org?.partner ?? null
  const workspace = useOptionalCurrentBrand()
  const router = useRouter()
  const current = getNavWorkspace(model.navWorkspace)
  const firstHrefFor = (id: NavWorkspaceId): string =>
    id === 'seo-geo' ? model.visibilityHref : model.dashboardHref

  const select = (id: NavWorkspaceId) => {
    if (workspace) storeNavWorkspace(workspace.orgSlug, workspace.brandSlug, id)
    if (id !== model.navWorkspace) router.push(firstHrefFor(id))
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('switchNavWorkspace')}
        className="hover:bg-sidebar-hover flex h-8 min-w-0 items-center gap-2 self-start rounded-md px-2 transition-colors"
      >
        <span className="flex size-4 shrink-0 -translate-y-[1px] items-center justify-center">
          <SokuLogoMark className="size-5 max-w-none" />
        </span>
        {/* One size for the whole lockup: the zone name used to be set
            smaller, which read as a typo beside the wordmark. 17px instead of
            `ProductWordmark`'s 19px so the longest pair ("Soku" + "SEO & GEO")
            still fits the 12.5rem rail next to the mark and the chevron. */}
        <span
          className={cn(
            LABEL,
            'flex min-w-0 translate-y-[1px] items-baseline gap-1.5 font-serif text-[17px] leading-7 font-normal tracking-tight',
          )}
        >
          <span className="text-[#ff7700]">{resolveProductName(partner, brand)}</span>
          <span className="text-sidebar-foreground truncate">
            {t(current.labelKey)}
          </span>
        </span>
        <ChevronDownIcon
          className={cn(LABEL, 'text-sidebar-foreground/60 size-3.5 shrink-0')}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 p-1.5">
        {NAV_WORKSPACES.map(item => (
          <DropdownMenuItem
            key={item.id}
            onSelect={() => select(item.id)}
            className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t(item.labelKey)}</div>
              <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed whitespace-normal">
                {t(item.descriptionKey)}
              </p>
            </div>
            {item.id === model.navWorkspace ? (
              <CheckIcon className="mt-0.5 size-4 shrink-0" />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function DesktopRail({
  model,
  t,
  user,
}: {
  model: RailModel
  t: ReturnType<typeof useTranslations>
  user: { name: string; email: string } | null
}) {
  const tHub = useTranslations('MarketplaceHub.tabs')
  const { product } = useProductBrand()
  const { state } = useSidebar()
  const collapsed = state === 'collapsed'
  const {
    scope,
    orgSections,
    homeHref,
    chatHref,
    filesHref,
    integrationsHref,
    marketplaceHref,
    mcpCliHref,
    automationsHref,
    settingsHref,
    showAutopilot,
    autopilotHref,
    showNavWorkspaceSwitcher,
    isChatActive,
    isFilesActive,
    isIntegrationsActive,
    isSkillMarketplaceActive,
    isMcpCliActive,
    isAutopilotActive,
    isAutomationsActive,
    isSettingsActive,
  } = model
  const zoneRows = workspaceRows(model, t)

  return (
    <>
      {/* px-2 matches the nav list's container padding; the lockup link adds
          the rows' own px-2 on top, so both columns start at the same 16px. */}
      <SidebarHeader className="px-2 pt-2 pb-0">
        {/* The trigger sits beside the lockup expanded and below the mark
            collapsed. It is absolutely positioned with every offset given as
            an animatable value in both states (left/top/translate, never
            `auto`), and the container's height/padding transition too — so at
            the collapse click nothing snaps; the button glides from the right
            edge down under the mark while the rail slides shut. pe-8 reserves
            the trigger's lane expanded so a long partner wordmark truncates
            before it. */}
        <div className="relative flex h-8 items-center pe-8 transition-[height,padding] duration-200 ease-linear group-data-[collapsible=icon]:h-16 group-data-[collapsible=icon]:pe-0">
          {showNavWorkspaceSwitcher ? (
            <NavWorkspaceSwitcher model={model} t={t} />
          ) : (
            <ProductWordmark homeHref={homeHref} />
          )}
          <SidebarTrigger className="text-sidebar-foreground/60 hover:bg-sidebar-hover hover:text-sidebar-foreground absolute top-0.5 left-[calc(100%-1.75rem)] size-7 transition-[left,top,translate] duration-200 ease-linear group-data-[collapsible=icon]:top-9 group-data-[collapsible=icon]:left-1/2 group-data-[collapsible=icon]:-translate-x-1/2" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <div className="flex flex-col gap-0.5 px-2 pt-0.5">
          {scope === 'org' ? (
            // Org-workspace routes swap the brand menu for the org menu
            // (Overview, Usage, Activity). Footer entries keep their
            // brand-scoped degradation and stay live.
            //
            // The swap is the whole nav, so without the row below the only
            // way back to the product is the brand switcher — a control you
            // have to know is also an exit. `chatHref` degrades to the org's
            // first brand here (org routes carry no brand), so it is always
            // live.
            <>
              <NavRow
                href={chatHref}
                label={t('backToBrand')}
                icon={BackArrowIcon}
                active={false}
                className="text-sidebar-foreground/60"
              />
              <div className="bg-sidebar-border/70 my-1 h-px" />
              {orgSections.flatMap(section =>
                section.items.map(item => (
                  <NavRow
                    key={item.slug}
                    href={item.href}
                    label={t(item.labelKey)}
                    icon={ORG_RAIL_ICONS[item.slug] ?? Building2}
                    active={item.active}
                  />
                )),
              )}
            </>
          ) : product === 'toolfare' ? (
            toolfareRows(model, t).map(row => <NavRow key={row.href} {...row} />)
          ) : (
            <>
              <NavRow
                href={chatHref}
                label={t('chat')}
                icon={MessageSquare}
                active={isChatActive}
              />
              <NavRow
                href={automationsHref}
                label={t('automations')}
                icon={Zap}
                active={isAutomationsActive}
              />
              {/* Sits next to Automations — both are "the agent works without
                  me"; Autopilot is the always-on end of that. Hidden unless
                  opted into under Settings → Labs. */}
              {showAutopilot ? (
                <NavRow
                  href={autopilotHref}
                  label={t('autopilot')}
                  icon={PlaneTakeoffIcon}
                  active={isAutopilotActive}
                />
              ) : null}
              {/* The workspace zone: Dashboard + Creatives under Ads (the
                  rail's historical middle), Visibility / Citations / Reports /
                  Sites under SEO & GEO. Swaps wholesale with the switcher. */}
              {zoneRows.map(row => (
                <NavRow key={row.href} {...row} />
              ))}
              <NavRow
                href={filesHref}
                label={t('files')}
                icon={FolderOpenIcon}
                active={isFilesActive}
              />
              <SetupSection
                label={t('setup')}
                items={[
                  {
                    href: integrationsHref,
                    label: tHub('integrations'),
                    icon: Cable,
                    active: isIntegrationsActive,
                  },
                  {
                    href: marketplaceHref,
                    label: t('skills'),
                    icon: Puzzle,
                    active: isSkillMarketplaceActive,
                  },
                  // Last in Setup, and deliberately below Skills: it is the
                  // only row about reaching this workspace from OUTSIDE Soku
                  // (an AI app over MCP, or the Soku CLI), which is a thing a
                  // customer does after the accounts and skills are in place.
                  {
                    href: mcpCliHref,
                    label: t('mcpCli'),
                    icon: PlugZap,
                    active: isMcpCliActive,
                  },
                ]}
              />
            </>
          )}
        </div>
      </SidebarContent>

      {/* px-0: the rows below carry the same px-2 as the nav list above, so
          the footer must not add a second inset or the two columns of text
          would not line up. */}
      <SidebarFooter className="px-0">
        {/* Settings sits directly on the brand/account row: both answer "who
            and what is this chrome configured for", and keeping them adjacent
            leaves the working list above unbroken. */}
        <div className="flex flex-col gap-0.5 px-2">
          <NavRow
            href={settingsHref}
            label={t('settings')}
            icon={Settings2}
            active={isSettingsActive}
          />
          {/* One row closes the rail: which brand am I in (left), who am I
              (right). Notifications live inside the account menu. Collapsed,
              the row becomes a column of two icons — the compact switcher
              (avatar + right-opening popover) above the account avatar. */}
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <ClientSwitcher compact side="right" />
              {user ? (
                <NavUser iconOnly name={user.name} email={user.email} />
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <div className="min-w-0 flex-1">
                <ClientSwitcher side="top" />
              </div>
              {user ? (
                <NavUser iconOnly name={user.name} email={user.email} />
              ) : null}
            </div>
          )}
        </div>
      </SidebarFooter>
    </>
  )
}

// ── Mobile labeled drawer ───────────────────────────────────────────────────
// The primitive's mobile Sheet is a wide off-canvas drawer (18rem) with its own
// chrome, so mobile renders the same information architecture in the sidebar
// primitive's own menu components rather than the desktop rows.

function MobileNav({
  model,
  t,
  user,
}: {
  model: RailModel
  t: ReturnType<typeof useTranslations>
  user: { name: string; email: string }
}) {
  const brand = useProductBrand()
  const partner = useOptionalActiveOrg()?.partner
  const tHub = useTranslations('MarketplaceHub.tabs')
  const {
    scope,
    orgSections,
    homeHref,
    chatHref,
    filesHref,
    integrationsHref,
    marketplaceHref,
    mcpCliHref,
    automationsHref,
    settingsHref,
    showAutopilot,
    autopilotHref,
    showNavWorkspaceSwitcher,
    isChatActive,
    isFilesActive,
    isIntegrationsActive,
    isSkillMarketplaceActive,
    isMcpCliActive,
    isAutopilotActive,
    isAutomationsActive,
    isSettingsActive,
  } = model

  const items: RailRow[] =
    scope === 'org'
      ? // Org-workspace routes swap the brand menu for the org menu
        // (Overview, Usage, Activity) — mirrors the desktop rail, exit row
        // included.
        [
          {
            href: chatHref,
            label: t('backToBrand'),
            icon: BackArrowIcon,
            active: false,
          },
          ...orgSections.flatMap(section =>
            section.items.map(item => ({
              href: item.href,
              label: t(item.labelKey),
              icon: ORG_RAIL_ICONS[item.slug] ?? Building2,
              active: item.active,
            })),
          ),
        ]
      : brand.product === 'toolfare'
        ? toolfareRows(model, t)
        : [
            {
              href: chatHref,
              label: t('chat'),
              icon: MessageSquare,
              active: isChatActive,
            },
            {
              href: automationsHref,
              label: t('automations'),
              icon: Zap,
              active: isAutomationsActive,
            },
            ...(showAutopilot
              ? [
                  {
                    href: autopilotHref,
                    label: t('autopilot'),
                    icon: PlaneTakeoffIcon,
                    active: isAutopilotActive,
                  },
                ]
              : []),
            ...workspaceRows(model, t),
            {
              href: filesHref,
              label: t('files'),
              icon: FolderOpenIcon,
              active: isFilesActive,
            },
            {
              href: integrationsHref,
              label: tHub('integrations'),
              icon: Cable,
              active: isIntegrationsActive,
            },
            {
              href: marketplaceHref,
              label: t('skills'),
              icon: Puzzle,
              active: isSkillMarketplaceActive,
            },
            {
              href: mcpCliHref,
              label: t('mcpCli'),
              icon: PlugZap,
              active: isMcpCliActive,
            },
          ]

  return (
    <>
      <SidebarHeader>
        <Link
          href={homeHref}
          aria-label={resolveProductName(partner, brand)}
          className="flex h-9 items-center gap-1 px-1"
        >
          <SokuLogoMark />
          <span className="translate-y-[2px] font-serif text-lg leading-none font-semibold tracking-tight text-orange-500 italic">
            {resolveProductName(partner, brand)}
          </span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="pt-0">
          <SidebarGroupContent>
            <div className="mb-1">
              <ClientSwitcher />
            </div>
            {showNavWorkspaceSwitcher ? (
              <div className="mb-1">
                <NavWorkspaceSwitcher model={model} t={t} />
              </div>
            ) : null}
            <SidebarMenu>
              {items.map(item => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={item.active}
                    className={cn(
                      item.active
                        ? 'bg-sidebar-accent font-medium'
                        : 'text-muted-foreground',
                    )}
                  >
                    <Link href={item.href}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isSettingsActive}
              className={cn(
                isSettingsActive
                  ? 'bg-sidebar-accent font-medium'
                  : 'text-muted-foreground',
              )}
            >
              <Link href={settingsHref}>
                <Settings2 />
                <span>{t('settings')}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <NavUser name={user.name} email={user.email} />
        </SidebarMenu>
      </SidebarFooter>
    </>
  )
}

// ── Entry ───────────────────────────────────────────────────────────────────

/**
 * Global navigation rail: a labeled column on desktop that collapses to an
 * icon rail (header trigger, Cmd+B, narrow-viewport auto-collapse; state
 * persists via the primitive's `sidebar_state` cookie), a labeled off-canvas
 * drawer on mobile. Chat history is not here — it lives in
 * `ChatHistoryPanel`, mounted by the chat route layout.
 *
 * Both desktop widths (12.5rem expanded / 3.5rem collapsed) are pinned on
 * `SidebarProvider` (in `AppSidebarShell`) rather than here: the gap element
 * that reserves horizontal space is a sibling of this `Sidebar`'s container,
 * so a CSS var set here would not reach it.
 */
export function AppRail() {
  const t = useTranslations('Sidebar')
  const { isMobile } = useSidebar()
  const model = useRailModel()
  const userContext = useUserContext()

  const emailPrefix = userContext?.email?.split('@')[0]
  const displayName =
    userContext?.fullName?.trim() || emailPrefix?.trim() || 'Unknown user'
  const user = userContext
    ? { name: displayName, email: userContext.email || 'Unknown email' }
    : null

  return (
    <Sidebar
      collapsible="icon"
      // `dark` scopes the whole rail subtree to the dark palette (not just the
      // --sidebar-* tokens) so rows that lean on generic tokens —
      // text-muted-foreground, borders, the user menu — stay legible on the
      // dark plate. Portalled dropdowns escape the subtree and stay light.
      // border-e-transparent kills the primitive's right divider — it used to
      // sit flush against the content and now floats visibly in the gutter.
      // The rail keeps one look everywhere, Autopilot included: the chrome is
      // how you navigate, and re-skinning it per destination made a routine
      // page change read as a mode switch.
      className="app-shell-rail dark [&_[data-slot=sidebar-inner]]:bg-sidebar border-e-transparent"
      // The desktop rail container is `fixed inset-y-0 h-svh`. When the
      // app-shell announcement banner is visible it sets --app-banner-offset
      // on a common ancestor; start the rail below the banner and let
      // bottom-0 (from inset-y-0) keep it anchored. Inline style so it
      // deterministically beats the primitive's utility classes.
      style={{ top: 'var(--app-banner-offset, 0px)', height: 'auto' }}
    >
      {isMobile ? (
        user ? (
          <MobileNav model={model} t={t} user={user} />
        ) : null
      ) : (
        <DesktopRail model={model} t={t} user={user} />
      )}
    </Sidebar>
  )
}
